// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import React, { MouseEvent, useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import {
  ByUid,
  byUid,
  OrganizationDocument,
  SupervisorDocument,
} from "../store/schema";
import { volCache } from "../store/api";
import { URLS, breadcrumbs } from "../urls";
import { update, useWidthGate } from "../UiUtil";
import { sortBy } from "../util";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { PageContainer } from "../components/PageContainer";
import { Column, Table as ReactTable } from "../components/Table";
import { Breadcrumbs } from "../components/BreadCrumb";

const WIDTH_GATE = {
  table: 501,
  email: 750,
  organization: 550,
  phone: 950,
};

type PageProps = RouteComponentProps<{}>;

const Page: React.FC<PageProps> = ({ history }) => {
  debug("======= SupervisorListPage =======");
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [state, setState] = useState(volCache().get());

  const { organizations, loaded, school, supervisors, user } = state;

  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    return volCache().subscribe(newState => {
      update("setState", state, setState, newState);
    });
  });

  const newSupervisor = () => history.push(URLS.vSupervisorsDetail(uuidv4()));

  const select = (e: MouseEvent, doc: SupervisorDocument) => {
    e.preventDefault();
    debug(`Navigating to supervisor ${doc.data.uid}`);
    history.push(URLS.vSupervisorsDetail(doc.data.uid));
  };

  return (
    <PageContainer
      title="Supervisors List"
      crumbs={breadcrumbs().vHome}
      administrators={state.administrators}
      school={school}
      checks={{ user, loaded }}
    >
      <div className="section">
        <button type="button" onClick={newSupervisor}>
          New Supervisor
        </button>
      </div>
      <div className="section">
        {widthBucket >= WIDTH_GATE.table ? (
          <SupervisorTable
            includeDeleted={includeDeleted}
            onSelect={select}
            orgs={byUid(organizations)}
            sups={supervisors}
            showEmail={widthBucket >= WIDTH_GATE.email}
            showOrganization={widthBucket >= WIDTH_GATE.organization}
            showPhone={widthBucket >= WIDTH_GATE.phone}
          />
        ) : (
            <SupervisorList
              includeDeleted={includeDeleted}
              onSelect={select}
              orgs={byUid(organizations)}
              sups={supervisors}
            />
          )}
      </div>
      {supervisors.some(x => x.data.isDeleted) && (
        <div className="nav">
          <button
            type="button"
            onClick={() => setIncludeDeleted(!includeDeleted)}
          >
            {includeDeleted ? "Hide" : "Show"} Deleted Supervisors
        </button>
        </div>
      )}
      <div className="section"></div>
      <Breadcrumbs crumbs={breadcrumbs().vHome} />
    </PageContainer>
  );
};
export const SupervisorListPage = withRouter(Page);

// ================================================================================

function orgName(orgs: ByUid<OrganizationDocument>, row: SupervisorDocument) {
  const org = orgs[row.data.organizationUid];
  return org == null ? "" : org.data.name;
}

// ================================================================================

interface SupervisorListProps {
  includeDeleted: boolean;
  onSelect: (e: MouseEvent, record: SupervisorDocument) => void;
  orgs: ByUid<OrganizationDocument>;
  sups: SupervisorDocument[];
}

const SupervisorList: React.FC<SupervisorListProps> = props => {
  const { includeDeleted, orgs, sups, onSelect } = props;
  const supervisors = sortBy(
    includeDeleted ? sups : sups.filter(x => !x.data.isDeleted),
    x => x.data.name
  );

  function itemClass(activity: SupervisorDocument): string {
    return activity.data.isDeleted ? "item deleted" : "item";
  }

  return (
    <ErrorBoundary>
      <div className="narrow-table">
        {supervisors.map(sup => (
          <div
            key={sup.data.uid}
            className={itemClass(sup)}
            onClick={e => onSelect(e, sup)}
          >
            <div className="title-statement">{sup.data.name}</div>
            <div className="statement">
              {sup.data.phone} | {sup.data.email}
            </div>
            <div className="statement">{orgName(orgs, sup)}</div>
          </div>
        ))}
      </div>
    </ErrorBoundary>
  );
};

// ================================================================================

interface SupervisorTableProps {
  includeDeleted: boolean;
  onSelect: (e: MouseEvent, record: SupervisorDocument) => void;
  orgs: ByUid<OrganizationDocument>;
  showEmail: boolean;
  showOrganization: boolean;
  showPhone: boolean;
  sups: SupervisorDocument[];
}

const SupervisorTable: React.FC<SupervisorTableProps> = props => {
  const {
    includeDeleted,
    onSelect,
    orgs,
    sups,
    showEmail,
    showOrganization,
    showPhone,
  } = props;
  const columns: Column<SupervisorDocument>[] = [
    {
      Header: "Name",
      id: "name",
      accessor: row => row.data.name,
      minWidth: 160,
    },
    {
      Header: "Email",
      id: "email",
      accessor: row => row.data.email,
      minWidth: 200,
      show: showEmail,
    },
    {
      Header: "Phone",
      id: "phone",
      accessor: row => row.data.phone,
      minWidth: 200,
      show: showPhone,
    },
    {
      Header: "Organization",
      id: "organization",
      accessor: row => orgName(orgs, row),
      minWidth: 200,
      show: showOrganization,
    },
  ];

  return (
    <ErrorBoundary>
      <ReactTable
        data={includeDeleted ? sups : sups.filter(x => !x.data.isDeleted)}
        columns={columns}
        isDeleted={x => !!x.data.isDeleted}
        onSelect={onSelect}
        show-pagination={false}
      />
    </ErrorBoundary>
  );
};

function debug(message: string) {
  if (true) {
    console.log(`SupervisorListPage: ${message}`);
  }
}
